import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * LOGOS — variants (row-grey · logo-cards-grid · violet-trust-bar)
 * Iter 175c — title · tagline · each logo name editable.
 * ════════════════════════════════════════════════════════════════════════ */
export function LogosBlockExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout || 'row-grey';
  const sourceField = Array.isArray(c.logos) ? 'logos' : 'items';
  const logos = c[sourceField] || Array.from({length:6}).map((_,i)=>({ name:`Brand ${i+1}` }));

  if (layout === 'logo-cards-grid') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto text-center">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Trusted by 500+ businesses'} tag="h2" className="text-2xl md:text-3xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-3 opacity-70 max-w-xl mx-auto" placeholder="Tagline" />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {logos.slice(0,8).map((l,i)=>(
              <div key={i} className="aspect-[3/2] border border-gray-200 rounded-xl flex items-center justify-center p-6" style={{ borderRadius: theme.radius || 12 }}>
                {l.image ? <SafeImg src={l.image} alt={l.name} className="max-h-12 object-contain" /> :
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={l.name} tag="span" className="text-sm font-bold opacity-70" placeholder="Brand" />}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'violet-trust-bar') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: '#3B1F8B' }}>
        <div className="max-w-5xl mx-auto text-white text-center">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Our Great Clients'} tag="h2" className="text-2xl md:text-3xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-3 opacity-80 max-w-xl mx-auto" placeholder="Tagline" />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
            {logos.slice(0,8).map((l,i)=>(
              <div key={i} className="flex items-center justify-center p-4 bg-white/8 rounded-lg">
                {l.image ? <SafeImg src={l.image} alt={l.name} className="max-h-10 object-contain brightness-200" /> :
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={l.name} tag="span" className="text-sm font-bold opacity-90" placeholder="Brand" />}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Portfolio — NEW `client-logos-wall` ──
  // Editorial 4×3 logo grid with client names as fallback + optional
  // "Case study →" hover links per cell. Trust-signal that doubles as
  // a portfolio index for agencies.
  if (layout === 'client-logos-wall') {
    const wall = logos.length >= 12 ? logos : [...logos, ...Array(Math.max(0, 12 - logos.length)).fill({ name: 'Client' })];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto text-center mb-12">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: theme.accent }}>{c.eyebrow || 'IN GOOD COMPANY'}</p>
          <E block={block} onEdit={onEdit} field="title" value={c.title || "Some of the brands we've built."} tag="h2" className="mt-3 text-3xl md:text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Trusted by" />
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: theme.ink + '18' }}>
          {wall.slice(0, 12).map((l, i) => (
            <div key={i} className="aspect-[4/3] flex flex-col items-center justify-center p-6 group cursor-pointer transition-colors" style={{ background: theme.bg }}>
              {l.image
                ? <SafeImg src={l.image} alt={l.name} className="max-h-10 object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all" />
                : <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={l.name} tag="span" className='text-lg font-bold opacity-60 group-hover:opacity-100 transition-opacity font-["Playfair_Display","Georgia",serif] italic' placeholder="Brand" />
              }
              {l.case_study && <span className="mt-2 text-[9px] uppercase tracking-widest opacity-0 group-hover:opacity-80 transition-opacity" style={{ color: theme.accent }}>Case study →</span>}
            </div>
          ))}
        </div>
      </section>
    );
  }

  // row-grey default
  return (
    <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-12" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-5xl mx-auto text-center">
        <E block={block} onEdit={onEdit} field="title" value={c.title || ''} tag="p" className="text-xs uppercase tracking-[0.2em] opacity-60 font-bold" placeholder="Eyebrow (optional)" />
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-6 grayscale opacity-60">
          {logos.slice(0,7).map((l,i)=>(
            <div key={i}>
              {l.image ? <SafeImg src={l.image} alt={l.name} className="max-h-8 object-contain" /> :
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={l.name} tag="span" className="text-sm font-bold" placeholder="Brand" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
